import React, { useMemo } from 'react';
import { useNextStep } from 'hooks/useStep';
import SurveyProgressBar from 'components/SurveyProgressBar';
import { useGetQuestions } from 'api/hooks/useSurveyQuestions';
import { useUser } from '../context/userContext';
import { getProgress, Section } from '../persistence/localProgress';
import { Maybe, QuestionItem } from '../__generated__/graphql';

const SectionBreak = ({ title, body, icon }: { title: string; body: string[]; icon?: string }) => {
  const { nextStep } = useNextStep();
  const { company, answers, jobRoleId } = useUser();
  const { questions } = useGetQuestions({ companyId: company?.id || '', jobRoleId });

  // Compute totals and answered counts across sections for the progress bar
  const totals: Record<Section, number> = useMemo(
    () => ({
      personality: (questions.personality || []).filter(Boolean).length,
      interests: (questions.interests || []).filter(Boolean).length,
      values: (questions.values || []).filter(Boolean).length,
    }),
    [questions],
  ) as Record<Section, number>;

  const answered = useMemo(() => {
    const companyId = company?.id;
    const sections: Section[] = ['personality', 'interests', 'values'];
    const counts = sections.reduce(
      (acc, s) => {
        const { answers: answersFromProgress } = getProgress(companyId, s);

        // Count from local progress (fast, persisted)
        const localCount = Object.keys(answersFromProgress || {}).length;

        // Count from server answers snapshot (fallback to capture last answer on navigation)
        const sectionIds = (questions[s] || [])
          .filter(Boolean)
          .map((q) => (q as Maybe<QuestionItem>)?.id)
          .filter(Boolean) as string[];
        const answersMap = (answers || {}) as unknown as Record<string, boolean | null>;
        const snapshotCount = sectionIds.reduce((n, qid) => (answersMap[qid] != null ? n + 1 : n), 0);

        acc[s] = Math.max(localCount, snapshotCount);
        return acc;
      },
      {} as Record<Section, number>,
    );
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id, questions, answers]);
  return (
    <div className="h-5/6	flex flex-col items-center justify-between  text-center text-white gap-20">
      <div className="text-white text-xl sm:text-display-sm font-semibold leading-xl sm:leading-display-sm mb-1 mt-5">{title}</div>
      <img className="w-16 h-16 text-white" alt="section-break-icon" src={icon} />
      {body.map((p, i) => (
        <p className="text-white" key={`p${i}`}>
          {p}
        </p>
      ))}

      <button className="bg-yellow-500 text-black 	p-2	uppercase rounded-lg font-semibold text-md " type="button" onClick={() => nextStep()}>
        Continue
      </button>
      <div className="w-full flex items-center justify-center">
        <SurveyProgressBar totals={totals} answered={answered} />
      </div>
    </div>
  );
};

export default SectionBreak;
